import React, { useState } from 'react';
import Grid2 from '@mui/material/Grid2';
import { FormControlLabel, Checkbox, Typography, Link, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import TextField from '../../../components/TextField';
import Picklist from '../../../components/Picklist';
import FormSection from '../../../components/FormSection';
import DocumentViewerDialog from '../../../components/DocumentViewerDialog';
import { CaregiverFormData } from '../types';
import { FieldErrors } from '../validation';

interface Props {
  formData: CaregiverFormData;
  fieldErrors: FieldErrors;
  handleChange: (e: { target: { name: string; value: any } }) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiSelectChange: (e: { target: { name: string; value: string[] } }) => void;
}

interface ViewerState {
  open: boolean;
  fetchUrl: string;
  title: string;
  initialPage?: number;
}

const CLOSED_VIEWER: ViewerState = { open: false, fetchUrl: '', title: '' };

const HumanResourcesStep: React.FC<Props> = ({
  formData,
  fieldErrors,
  handleChange,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation();
  const [viewer, setViewer] = useState<ViewerState>(CLOSED_VIEWER);

  const isAlien = formData.citizenStatus === 'An alien authorized to work';
  const isPermanentResident = formData.citizenStatus === 'A lawful permanent resident';
  const isRnOrLpn = formData.type === 'RN' || formData.type === 'LPN';

  const openTemplate = (templateName: string, title: string, initialPage?: number) =>
    setViewer({
      open: true,
      fetchUrl: `/api/templates/static/${encodeURIComponent(templateName)}?formName=HHA`,
      title,
      initialPage,
    });

  return (
    <>
      <DocumentViewerDialog
        open={viewer.open}
        onClose={() => setViewer(CLOSED_VIEWER)}
        fetchUrl={viewer.fetchUrl}
        title={viewer.title}
        initialPage={viewer.initialPage}
      />

      <FormSection title={t('form.humanResources', 'Human Resources')} icon={<AssignmentIndIcon />}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="ssn"
              label={t('form.ssn', 'Social Security Number')}
              value={formData.ssn}
              onChange={handleChange}
              inputMask="999-99-9999"
              error={!!fieldErrors.ssn}
              helperText={fieldErrors.ssn}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="taxClassification"
              value={formData.taxClassification}
              onChange={handleChange}
              label={t('form.taxClassification', "Tax Classification (If you're not sure, select Individual)")}
              objectName="caregiver"
              required
              error={!!fieldErrors.taxClassification}
              helperText={fieldErrors.taxClassification}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="homecareExperienceYears"
              value={formData.homecareExperienceYears}
              onChange={handleChange}
              label={t('form.homecareExperienceYears', 'How many years of experience in Homecare do you have?')}
              objectName="caregiver"
              required
              error={!!fieldErrors.homecareExperienceYears}
              helperText={fieldErrors.homecareExperienceYears}
            />
          </Grid2>
        </Grid2>
      </FormSection>

      <FormSection
        title={t('form.i9Verification', 'I-9 Verification')}
        icon={<VerifiedUserIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            size="small"
            onClick={() => openTemplate('I-9', t('form.i9Verification', 'I-9 Verification'))}
          >
            {t('form.viewDocument', 'View Document')}
          </Button>
        }
      >
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography>
              {t('form.i9Text1a', 'Every new hire is required by law to verify both identity and eligibility to work in the U.S. To do this, you will need to provide documents that support your employment eligibility. The I-9 verification process will also require your signature. For more information, see the ')}
              <Link href="https://www.uscis.gov/i-9" target="_blank" rel="noreferrer">
                {t('form.i9Text1Link', "Department of Homeland Security's article")}
              </Link>
              {t('form.i9Text1b', ' on the provisions of the I-9.')}
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Typography>
              {t('form.i9Text2', 'I am aware that federal law provides for imprisonment and/or fines for false statements or use of false documents in connection with the completion of this form.')}
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Typography>
              {t('form.i9Text3', 'I attest, under penalty of perjury, that I am (select one of the following):')}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="citizenStatus"
              value={formData.citizenStatus}
              onChange={handleChange}
              label={t('form.citizenStatus', 'Citizen Status')}
              objectName="caregiver"
              required
              error={!!fieldErrors.citizenStatus}
              helperText={fieldErrors.citizenStatus}
            />
          </Grid2>

          {isPermanentResident && (
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="uscisNumber"
                label={t('form.uscisNumber', 'USCIS Number')}
                value={formData.uscisNumber}
                onChange={handleChange}
                error={!!fieldErrors.uscisNumber}
                helperText={fieldErrors.uscisNumber}
                inputProps={{ maxLength: 9 }}
              />
            </Grid2>
          )}

          {isAlien && (
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Picklist
                fieldName="noncitizenDocumentType"
                value={formData.noncitizenDocumentType}
                onChange={handleChange}
                label={t('form.noncitizenDocumentType', 'Work Authorization Document')}
                objectName="caregiver"
                required
                error={!!fieldErrors.noncitizenDocumentType}
                helperText={fieldErrors.noncitizenDocumentType}
              />
            </Grid2>
          )}

          {isAlien && formData.noncitizenDocumentType === 'USCIS Number' && (
            <>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="uscisNumber"
                  label={t('form.uscisNumber', 'USCIS Number')}
                  value={formData.uscisNumber}
                  onChange={handleChange}
                  error={!!fieldErrors.uscisNumber}
                  helperText={fieldErrors.uscisNumber}
                  inputProps={{ maxLength: 9 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="noncitizenExpirationDate"
                  label={t('form.noncitizenExpirationDate', 'Expiration Date')}
                  type="date"
                  value={formData.noncitizenExpirationDate}
                  onChange={handleChange}
                  error={!!fieldErrors.noncitizenExpirationDate}
                  helperText={fieldErrors.noncitizenExpirationDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid2>
            </>
          )}

          {isAlien && formData.noncitizenDocumentType === 'Form I-94 Admission Number' && (
            <>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="formI94AdmissionNumber"
                  label={t('form.formI94AdmissionNumber', 'Form I-94 Admission Number')}
                  value={formData.formI94AdmissionNumber}
                  onChange={handleChange}
                  error={!!fieldErrors.formI94AdmissionNumber}
                  helperText={fieldErrors.formI94AdmissionNumber}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="noncitizenExpirationDate"
                  label={t('form.noncitizenExpirationDate', 'Expiration Date')}
                  type="date"
                  value={formData.noncitizenExpirationDate}
                  onChange={handleChange}
                  error={!!fieldErrors.noncitizenExpirationDate}
                  helperText={fieldErrors.noncitizenExpirationDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid2>
            </>
          )}

          {isAlien && formData.noncitizenDocumentType === 'Foreign Passport' && (
            <>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  name="foreignPassportNumber"
                  label={t('form.foreignPassportNumber', 'Foreign Passport Number')}
                  value={formData.foreignPassportNumber}
                  onChange={handleChange}
                  error={!!fieldErrors.foreignPassportNumber}
                  helperText={fieldErrors.foreignPassportNumber}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Picklist
                  fieldName="foreignPassportCountry"
                  value={formData.foreignPassportCountry}
                  onChange={handleChange}
                  label={t('form.foreignPassportCountry', 'Foreign Passport Country of Issuance')}
                  objectName="caregiver"
                  required
                  error={!!fieldErrors.foreignPassportCountry}
                  helperText={fieldErrors.foreignPassportCountry}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  name="noncitizenExpirationDate"
                  label={t('form.noncitizenExpirationDate', 'Expiration Date')}
                  type="date"
                  value={formData.noncitizenExpirationDate}
                  onChange={handleChange}
                  error={!!fieldErrors.noncitizenExpirationDate}
                  helperText={fieldErrors.noncitizenExpirationDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid2>
            </>
          )}
        </Grid2>
      </FormSection>

      {isRnOrLpn && (
        <FormSection
          title={t('form.federalWithholding', 'Federal Withholding')}
          icon={<AccountBalanceIcon />}
          action={
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              size="small"
              onClick={() => openTemplate('W-4', t('form.federalWithholding', 'Federal Withholding'))}
            >
              {t('form.viewDocument', 'View Document')}
            </Button>
          }
        >
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Picklist
                fieldName="federalFilingStatus"
                value={formData.federalFilingStatus}
                onChange={handleChange}
                label={t('form.federalFilingStatus', 'Federal Filing Status')}
                objectName="caregiver"
                required
                error={!!fieldErrors.federalFilingStatus}
                helperText={fieldErrors.federalFilingStatus}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="dependentsUnderAge17"
                label={t('form.dependentsUnderAge17', 'Dependents Under Age 17')}
                value={formData.dependentsUnderAge17}
                onChange={handleChange}
                inputMask="999999999999999999"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="fedDeductions"
                label={t('form.fedDeductions', 'Federal Deductions')}
                value={formData.fedDeductions}
                onChange={handleChange}
                inputMask="999999999999999999"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="otherDependents"
                label={t('form.otherDependents', 'Other Dependents')}
                value={formData.otherDependents}
                onChange={handleChange}
                inputMask="999999999999999999"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="fedAdditionalWithholdingAmount"
                label={t('form.fedAdditionalWithholdingAmount', 'Federal Additional Withholding Amount')}
                value={formData.fedAdditionalWithholdingAmount}
                onChange={handleChange}
                inputMask="999999999999999999"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="otherIncome"
                label={t('form.otherIncome', 'Other Income')}
                value={formData.otherIncome}
                onChange={handleChange}
                inputMask="999999999999999999"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.multipleJobs}
                    onChange={handleCheckboxChange}
                    name="multipleJobs"
                  />
                }
                label={t('form.multipleJobs', 'Multiple Jobs or Spouse Works')}
              />
            </Grid2>
          </Grid2>
        </FormSection>
      )}
    </>
  );
};

export default HumanResourcesStep;
